import { stripUnit, unit } from '../styles/unit';

export interface SizeObject {
    width: number | string;
    height: number | string;
}

/**
 * Parse strings.
 * @example
 * ```javascript
 * const string = '28';
 * console.log(string); // string '28';
 * console.log(parse(string)); // int 28;
 * ```
 * @param data
 * @return {*}
 */
export function parse<T = any>(data: any): T {
    if (typeof data === 'string') {
        switch (data) {
            case 'null':
                return null as any;
            case 'undefined':
                return undefined as any;
            default:
                try {
                    return JSON.parse(`[${data}]`)[0];
                } catch (e) {
                    return data as any;
                }
        }
    }

    return data;
}

/**
 * Parse Values of an object (doing str -> int/bool/array/object)
 * @param object
 */
export function parseObject(object: Record<string, any>) {
    const keys = Object.keys(object);

    keys.forEach((key) => {
        const value = object[key];
        object[key] = value && typeof value === 'object' ? parseObject(value) : parse(value);
    });

    return object;
}

/**
 * Parse sizes
 * @param size
 * @param _unit
 */
export function parseSize(size: number | string | (number | string)[] | SizeObject, _unit = 'px') {
    let width: any;
    let height: any;

    switch (true) {
        case typeof size === 'number':
            width = size;
            height = size;
            break;
        case typeof size === 'string' && !!size:
            let values: (number | string)[] = [0, 0];

            size = `${size}`.trim();

            let parts = size.match(/([^\sx]*)[\sx](.*)$/) || [];

            if (parts.length !== 3) {
                parts = [size, size, size];
            }

            const part = parts[1];

            values = (parts.length === 3 ? [part, parts[2] || part] : [part, part])
                .map((value) => value.trim())
                .map((value) => stripUnit(value));

            width = values[0];
            height = values[1];
            break;
        case Array.isArray(size):
            const size$: any[] = size as any;
            width = stripUnit(size$[0]);
            height = stripUnit(size$[1]);
            break;
        case typeof size === 'object':
            width = stripUnit((size as any).width) || 0;
            height = stripUnit((size as any).height) || 0;
            break;
        default:
            width = 0;
            height = 0;
    }

    return {
        width,
        height,
        widthSize: unit(width, _unit),
        heightSize: unit(height, _unit),
        max: Math.max(width, height),
        min: Math.min(width, height),
    };
}
